// Ported from dev-scrips-pm-react src/services/scheduling/use-update-appointment.ts (PROD-1190 calendar port)
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { put } from '@/shared/api/api-client';
import type { EditRecurringPayload } from '@/scheduling/components/appointments/edit-recurring-scope-dialog';
import type {
  AppointmentResponse,
  AppointmentsDetailsResponse,
  CreateAppointmentRequest,
  PreLoadAppointmentResponse,
} from '@/types/scheduling-api';

/**
 * PUT /api/Appointment/{appointmentId} — full-appointment update.
 *
 * Mirrors Flutter `updateAppointment` in
 * `scheduling_data_source_impl.dart:302-330`. The request body is the
 * same shape `useBookAppointment` ships (Flutter's `Appointment.toJson()`
 * on Book and Update); the only difference is verb + URL.
 *
 * Cache strategy (Samer review #67 §1, round 2):
 *
 *   1. onMutate — apply an OPTIMISTIC patch to:
 *        - the panel's PreLoad cache (`['appointment-details', …]`)
 *        - every cached appointments list (`['appointments', …]`)
 *      AND snapshot both so we can roll back on error. The UI flips to
 *      the new values immediately — no stale-data flash during the
 *      refetch round-trip that `invalidateQueries` triggers.
 *
 *   2. onError — restore the snapshots verbatim. The caller's toast
 *      flow surfaces the failure separately.
 *
 *   3. onSettled — invalidate both prefix keys to force a refetch and
 *      reconcile whatever the server actually persisted (the optimistic
 *      patch covers free-text + status fields, but the server may also
 *      adjust derived fields like `updatedDate` that we can't predict).
 *
 * Mirrors the optimistic pattern in `useUpdateAppointmentStatus` —
 * matching the codebase convention so the two update hooks behave
 * identically from a cache perspective.
 *
 * Permission enforcement (Samer #67 §5, round 2):
 *   `put` goes through `@/shared/api/api-client` which unconditionally
 *   injects the bearer token + organizationId/practiceid/UserId/RoleId
 *   headers. There is no `skipAuth` / anonymous path in this hook. The
 *   server enforces `Manage_Patient_Appointments { edit: true }`
 *   on the same headers — the UI gate is a UX aid, not the security
 *   boundary.
 *
 * UA app uses a different endpoint (`/Reschedule`) — out of scope for
 * this React port (we only target PM).
 */

interface UpdateResponse {
  id?: string;
}

interface UpdateAppointmentVariables {
  appointmentId: string;
  body: CreateAppointmentRequest;
  /**
   * Recurring-series scope, when the consumer wants the edit to
   * propagate across the series. Built via
   * `buildEditRecurringPayload(scope, occurrenceStartISO, dataUpdate)`
   * in the EditRecurringScopeDialog module.
   *
   *   - `undefined` / `null` → "this-only" — server treats the PUT as a
   *      single-occurrence edit.
   *   - `{ allAppointment: false, appointmentDate, dataUpdate }` →
   *      "this and following" — server propagates the changed fields
   *      from `appointmentDate` onward.
   *   - `{ allAppointment: true,  appointmentDate: null, dataUpdate }` →
   *      "all in series" — server propagates the changed fields across
   *      every occurrence.
   *
   * Wire shape verified via dev curl 2026-05-13 (see PR #100).
   */
  editRecurring?: EditRecurringPayload | null;
}

interface OptimisticContext {
  /**
   * Snapshots of every query key we mutated, captured before the
   * optimistic patch. `onError` restores these verbatim.
   */
  snapshots: { key: readonly unknown[]; data: unknown }[];
}

export function useUpdateAppointment() {
  const queryClient = useQueryClient();

  return useMutation<UpdateResponse, Error, UpdateAppointmentVariables, OptimisticContext>({
    mutationFn: ({ appointmentId, body, editRecurring }) => {
      // editRecurring is a top-level field on the PUT body when present.
      // Pass it through whatever scope the caller picked; `null` is the
      // wire-canonical "this-only" form so we forward as-is rather than
      // stripping it from the request.
      const wireBody =
        editRecurring === undefined
          ? body
          : ({ ...body, editRecurring } as CreateAppointmentRequest & {
              editRecurring: EditRecurringPayload | null;
            });
      return put<UpdateResponse>(`/api/Appointment/${appointmentId}`, wireBody);
    },

    onMutate: ({ appointmentId, body }) => {
      const snapshots: OptimisticContext['snapshots'] = [];

      // Source-row optimistic patch fires on EVERY scope, including
      // series-wide. The patch only targets the row whose `id` matches
      // `appointmentId` — i.e., the occurrence the user actually clicked
      // / dragged. Other rows in the same recurring series (which the
      // server WILL update on `following` / `all` scope) get refreshed
      // through invalidate-on-settled. The honest UX trade-off: the
      // dragged tile flips immediately so the gesture doesn't look
      // silently failed; the rest of the series catches up on the
      // refetch round-trip (Samer review on PR #123 §4).

      // 1. PreLoad cache (panel) — replace `appointment` with the new body.
      queryClient
        .getQueryCache()
        .findAll({ queryKey: ['appointment-details'] })
        .forEach((q) => {
          snapshots.push({ key: q.queryKey, data: q.state.data });
        });

      queryClient.setQueriesData<PreLoadAppointmentResponse | undefined>(
        { queryKey: ['appointment-details'] },
        (prev) => {
          if (!prev?.appointment) return prev;
          if (prev.appointment.id !== appointmentId) return prev;
          // Merge — preserves preload-only fields (billingProfiles,
          // patientData, etc.) that aren't in the update body.
          return {
            ...prev,
            appointment: {
              ...prev.appointment,
              ...(body as unknown as Partial<AppointmentResponse>),
            } as AppointmentResponse,
          };
        },
      );

      // 2. Appointments lists (calendar) — replace the row in-place.
      queryClient
        .getQueryCache()
        .findAll({ queryKey: ['appointments'] })
        .forEach((q) => {
          snapshots.push({ key: q.queryKey, data: q.state.data });
        });

      queryClient.setQueriesData<AppointmentsDetailsResponse | undefined>(
        { queryKey: ['appointments'] },
        (prev) => {
          if (!prev?.appointments) return prev;
          const idx = prev.appointments.findIndex((a) => a.id === appointmentId);
          if (idx < 0) return prev;
          // The list response uses `ListAppointmentResponse` (narrower
          // than the full AppointmentResponse) — we only overlay the
          // overlapping fields that change (status / start / end /
          // comment / reasonCode etc.) Cast as `unknown` because the
          // body shape is wider than the list row.
          const next = prev.appointments.slice();
          const merged = {
            ...next[idx],
            ...(body as unknown as Record<string, unknown>),
          };
          next[idx] = merged as (typeof next)[number];
          return { ...prev, appointments: next };
        },
      );

      return { snapshots };
    },

    onError: (_err, _vars, ctx) => {
      // Roll back every cache key we touched. Caller surfaces the
      // failure via toast separately.
      ctx?.snapshots.forEach(({ key, data }) =>
        queryClient.setQueryData(key, data),
      );
    },

    onSettled: () => {
      // Reconcile with the server's actual persisted state — covers
      // derived fields (updatedDate / updatedPerson / etc.) that the
      // optimistic patch can't predict.
      queryClient.invalidateQueries({ queryKey: ['appointment-details'] });
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
    },
  });
}
